import { createRouter, createWebHistory } from 'vue-router'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            redirect: () => {
                // store is imported at the bottom, but due to hoisting/module loading, we need to be careful.
                // However, since this is a callback executed on navigate, the module should be evaluated.
                // To be safe and avoid circular dependency issues with store importing router, 
                // we can assume 'store' from the outer scope is available by the time this runs.
                if (coreState.activeUser) {
                    // Logic from getNextStep
                    // Since we can't easily import the helper here without circular deps or module issues if not careful,
                    // we will inline the logic using the helper if possible, or just rely on 'start' vs 'home'.
                    // Wait, we can import it!
                    return { name: 'home' } // Temporarily keep simple, or:
                    // Ideally: const path = getNextStep(coreState.activeUser, members);
                    // But accessing 'members' from store might be tricky if not reactive or indexed nicely.
                    // Actually, let's trust SignUpFlow's handling for now, OR:
                    // If we want to be strict, we check onboarding_intent here.

                    /* STRICT MODE IMPLEMENTATION */
                    /*
                    const members = [];
                    for (const m of coreState.members.values()) {
                        if (m.userId === coreState.activeUser.id) members.push(m);
                    }
                    if (!coreState.activeUser.onboarding_intent) return { name: 'start' };
                    if (coreState.activeUser.onboarding_intent === 'merchant' && members.length === 0) return { name: 'start' };
                    return { name: 'home' };
                    */

                    // For now, let's leave it as 'home' because 'start' route guard handles the rest?
                    // No, preventing 'direct to dashboard' requires this check.

                    const members = [];
                    for (const m of coreState.members.values()) {
                        if (m.userId === coreState.activeUser.id) members.push(m);
                    }
                    if (!coreState.activeUser.onboarding_intent) {
                        return { name: 'start' };
                    }
                    if (coreState.activeUser.onboarding_intent === 'merchant' && members.length === 0) {
                        return { name: 'start' };
                    }
                    return { name: 'home' };

                } else {
                    return { name: 'start' }
                }
            }
        },
        {
            path: '/',
            component: () => import('../layouts/MainLayout.vue'),
            children: [
                {
                    path: 'home',
                    name: 'home',
                    component: () => import('../views/dashboard/HomeView.vue')
                },
                {
                    path: 'sites',
                    name: 'sites',
                    component: () => import('../views/dashboard/SitesView.vue')
                },
                {
                    path: 'library',
                    name: 'library',
                    component: () => import('../views/dashboard/LibraryView.vue')
                },
                {
                    path: 'shop',
                    name: 'shop',
                    component: () => import('../views/dashboard/ShopView.vue')
                },
                {
                    path: 'profile/:id?',
                    name: 'profile',
                    component: () => import('../views/dashboard/ProfileView.vue')
                },
                {
                    path: 'team',
                    name: 'team',
                    component: () => import('../views/dashboard/TeamView.vue')
                },
                {
                    path: 'network',
                    name: 'network',
                    component: () => import('../views/dashboard/NetworkView.vue')
                },
                {
                    path: 'settings',
                    name: 'settings',
                    component: () => import('../views/dashboard/SettingsView.vue')
                }
            ]
        },
        {
            path: '/join',
            name: 'join',
            component: () => import('../views/JoinRedirect.vue')
        },
        {
            path: '/partner-invite',
            name: 'partner-invite',
            component: () => import('../views/PartnerInviteRedirect.vue')
        },
        {
            path: '/embed/map',
            name: 'map-embed',
            component: () => import('../views/embed/MapView.vue')
            // No MainLayout here, so it renders full screen
        },
        {
            path: '/embed/map/:shopId',
            name: 'widget',
            component: () => import('../views/WidgetView.vue')
        },
        {
            path: '/start',
            name: 'start',
            component: () => import('../views/auth/SignupView.vue')
        },
        {
            path: '/setup/map/:shopId',
            name: 'map-setup',
            component: () => import('../views/MapBuilderView.vue')
        },
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: () => import('../views/auth/ForgotPasswordView.vue')
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: () => import('../views/auth/ResetPasswordView.vue')
        }
    ]
})

import { coreState } from '../stores/core.js'

import { getNextStep } from '../composables/useOnboarding.js'

router.beforeEach((to, from, next) => {
    console.log(`[Router] Navigation to ${to.name}. ActiveUser: ${coreState.activeUser?.id}`);

    // SECURITY: Prevent navigation out of Embed View to Main App
    if ((from.name === 'map-embed' || from.name === 'widget') && (to.name !== 'map-embed' && to.name !== 'widget')) {
        return next(false)
    }

    // Auth & Completion Guard
    if (coreState.activeUser) {
        // Calculate Onboarding Status
        const members = [];
        for (const m of coreState.members.values()) {
            if (m.userId === coreState.activeUser.id) members.push(m);
        }
        const nextStepPath = getNextStep(coreState.activeUser, members);
        console.log(`[RouterDebug] User: ${coreState.activeUser.id}, Intent: ${coreState.activeUser.onboarding_intent}, Completed: ${coreState.activeUser.signupCompleted}`);
        console.log(`[RouterDebug] NextStep: ${nextStepPath}, Members: ${members.length}`);

        const isOnboardingIncomplete = nextStepPath.startsWith('/onboarding');

        console.log(`[Router] To: ${to.name}, NextStep: ${nextStepPath}, Incomplete: ${isOnboardingIncomplete}`);

        if (to.name === 'start') {
            // RELAXED GUARD: Allow users to stay on /start to see "Success/Welcome" screen.
            // Only redirect if they explicitly navigate away or via button.
            // if (!isOnboardingIncomplete) {
            //    console.log("[Router] Already complete. Redirecting to home.");
            //    return next({ name: 'home' })
            // }
            // Otherwise, they belong here.
            return next()
        } else {
            // Trying to access internal dashboard pages
            // If they are incomplete, force them back to /start
            const isExplicitlyComplete = coreState.activeUser.signupCompleted === true;

            // Allow map-setup route during onboarding (it's part of the flow)
            if (isOnboardingIncomplete && !isExplicitlyComplete && to.name !== 'map-embed' && to.name !== 'widget' && to.name !== 'map-setup') {
                console.log("[Router] Incomplete onboarding. Redirecting to start.");
                return next({ name: 'start' })
            }
            return next()
        }
    } else {
        // Not Authenticated
        // Allow public pages: start, map-embed, forgot-password, reset-password, terms-of-service, privacy-policy
        if (to.name !== 'start' && to.name !== 'map-embed' && to.name !== 'widget' && to.name !== 'join' && to.name !== 'forgot-password' && to.name !== 'reset-password' && to.name !== 'terms-of-service' && to.name !== 'privacy-policy') {
            return next({ name: 'start' })
        }
        return next()
    }
})

export default router
